import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { EventBus, useGameStore, type ProximityMessage } from '@campus/core';
import { SidePanel, panelHeaderStyle } from '../people/SidePanel.js';
import { sessionInfo } from '../people/membersStore.js';
import { MessageText } from '../messaging/Mentions.js';
import { replaceFinalSmiley } from '../../messaging/smileys.js';
import { useBubbleChatStore } from '../../stores/bubbleChatStore.js';
import { Button, Icon, Switch } from '../ds/index.js';

/** Même longueur que le serveur (apps/realtime/src/lib/meetingChat.ts). */
const MAX_LENGTH = 500;
/** Deux messages d'une même personne à moins de 5 min : un seul en-tête (comme Meet). */
const GROUP_MS = 5 * 60_000;

const hhmm = (ts: number) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/**
 * Chat d'une salle de réunion, façon Google Meet : panneau pleine hauteur à
 * droite, qui reste ouvert tant qu'on ne le ferme pas. Ceux qui arrivent en
 * cours de réunion voient les messages déjà envoyés ; tout est effacé quand la
 * salle se vide (rien en base). Les admins épinglent un message et peuvent
 * réserver l'écriture aux admins. Entrée = envoyer, Maj+Entrée = nouvelle ligne.
 */
export function MeetingChatPanel({ roomName, onClose }: { roomName: string; onClose: () => void }) {
  const messages = useGameStore((s) => s.proximityMessages);
  const meeting = useBubbleChatStore((s) => s.meeting);
  const { role } = sessionInfo();
  const isAdmin = role === 'OWNER' || role === 'ADMIN';
  const locked = !!meeting?.locked;
  const canWrite = !locked || isAdmin;
  const pinned = meeting?.pinnedId ? messages.find((m) => m.id === meeting.pinnedId) ?? null : null;

  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Reste en bas tant qu'on y est ; ne saute pas si l'on relit plus haut.
  const atBottom = useRef(true);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Saisie qui grandit avec le texte (5 lignes au plus).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);

  useEffect(() => { if (canWrite) inputRef.current?.focus(); }, [canWrite]);

  function send() {
    const value = replaceFinalSmiley(text).trim();
    if (!value || !canWrite) return;
    EventBus.emit('ui:chat-bubble-send', { text: value.slice(0, MAX_LENGTH) });
    setText('');
    atBottom.current = true;
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  }

  function jumpTo(id: string) {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-msg="${CSS.escape(id)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('is-flash');
    setTimeout(() => el.classList.remove('is-flash'), 1600);
  }

  const header = (
    <header style={{ ...panelHeaderStyle, flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, font: '700 16px/1.25 var(--font-display)' }}>Messages de la réunion</h2>
          <div style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roomName}</div>
        </div>
        <Button variant="ghost" size="sm" icon="x" aria-label="Fermer les messages" onClick={onClose} />
      </div>
      {isAdmin && (
        <Switch
          label="Autoriser les participants à envoyer des messages"
          checked={!locked}
          onChange={(on) => EventBus.emit('ui:meeting-chat-lock', { locked: !on })}
        />
      )}
    </header>
  );

  return (
    <SidePanel label="Messages de la réunion" onClose={onClose} header={header} footer={
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px 12px', borderTop: '1px solid var(--color-border-default)' }}
      >
        {canWrite ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              className="pl-input pl-grow"
              aria-label="Envoyer un message à la réunion"
              placeholder="Envoyer un message"
              rows={1}
              maxLength={MAX_LENGTH}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              style={{ resize: 'none', minHeight: 40, lineHeight: 1.4, padding: '9px 12px', fontFamily: 'inherit' }}
            />
            <Button variant="primary" type="submit" icon="send" aria-label="Envoyer" disabled={!text.trim()} style={{ height: 40 }} />
          </div>
        ) : (
          <p className="pl-help" style={{ margin: 0 }}>Seuls les admins peuvent envoyer des messages dans cette réunion.</p>
        )}
        {text.length > MAX_LENGTH - 100 && (
          <span style={{ alignSelf: 'flex-end', fontSize: 11, color: 'var(--color-text-secondary)' }}>{text.length} / {MAX_LENGTH}</span>
        )}
      </form>
    }>
      {pinned && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '10px 12px 0', padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-raised)', border: '1px solid var(--color-border-default)' }}>
          <Icon name="pin" size={14} className="pl-menu-icon" />
          <button type="button" onClick={() => jumpTo(pinned.id!)} style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)' }}>Épinglé · {pinned.isSelf ? 'Toi' : pinned.displayName}</div>
            <div style={{ fontSize: 'var(--text-sm)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{pinned.text}</div>
          </button>
          {isAdmin && <Button variant="ghost" size="sm" icon="x" aria-label="Retirer l'épingle" onClick={() => EventBus.emit('ui:meeting-chat-pin', { messageId: null })} />}
        </div>
      )}
      <div
        ref={listRef}
        onScroll={(e) => { const el = e.currentTarget; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column' }}
      >
        <p style={{ margin: '0 0 12px', padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-raised)', fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
          Les messages ne sont visibles que des personnes présentes dans la salle, y compris celles qui arrivent après. Ils sont effacés quand tout le monde est parti.
        </p>
        {messages.length === 0 && (
          <p style={{ margin: 'auto 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Aucun message pour l&apos;instant.</p>
        )}
        {messages.map((m, i) => (
          <MeetingMessage
            key={m.id ?? `${m.senderId}-${m.timestamp}-${i}`}
            msg={m}
            grouped={i > 0 && messages[i - 1]!.senderId === m.senderId && m.timestamp - messages[i - 1]!.timestamp < GROUP_MS}
            pinned={!!m.id && m.id === meeting?.pinnedId}
            canPin={isAdmin && !!m.id}
          />
        ))}
      </div>
    </SidePanel>
  );
}

function MeetingMessage({ msg, grouped, pinned, canPin }: { msg: ProximityMessage; grouped: boolean; pinned: boolean; canPin: boolean }) {
  return (
    <div className="pl-meetchat-msg" data-msg={msg.id} style={{ marginTop: grouped ? 4 : 14 }}>
      {!grouped && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 'var(--text-label)', fontWeight: 700 }}>{msg.isSelf ? 'Toi' : msg.displayName}</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{hhmm(msg.timestamp)}</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-sm)', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          <MessageText text={msg.text} />
        </span>
        {canPin && (
          <button
            type="button"
            className={`pl-meetchat-act${pinned ? ' is-on' : ''}`}
            aria-label={pinned ? 'Retirer l’épingle' : 'Épingler le message'}
            title={pinned ? 'Retirer l’épingle' : 'Épingler en haut du chat'}
            onClick={() => EventBus.emit('ui:meeting-chat-pin', { messageId: pinned ? null : msg.id! })}
          >
            <Icon name="pin" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
